import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../header/Header";
import Axios from "axios";
import moment from "moment";
import {
  LuBriefcase,
  LuChevronLeft,
  LuChevronRight,
  LuClipboardList,
  LuPalmtree,
  LuSearch,
  LuStickyNote,
  LuUserX,
  LuUsers,
} from "react-icons/lu";
import { INPUT } from "../ui/ui";
import { DateFilter, FilterIconButton } from "../ui/DateFilter";
import "../taskList/TaskList.css";
const config = require("../../config/config");
const queryString = require("query-string");

const DAY = "YYYY-MM-DD";
const OPTIONAL_HOLIDAY_TYPE = 2;
// Status ids that mean the person is away for the day (paid / optional leave)
const LEAVE_STATUS_IDS = [config.EARNED_LEAVE, config.OPTIONAL_HOLIDAY, config.WORK_WITHOUT_PAY];
const LONG_TASK_LINES = 8;

const normName = (s) => String(s || "").trim().toLowerCase();

const displayName = (name, email) => {
  if (name && String(name).trim() && !String(name).includes("@")) return name;
  const source = String(name || email || "");
  if (!source.includes("@")) return source || "Unknown";
  return source
    .substring(0, source.lastIndexOf("@"))
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const TaskList = () => {
  const [date, setDate] = useState(moment().format(DAY));
  const [entries, setEntries] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [holidaysByYear, setHolidaysByYear] = useState({}); // { "2026": [...] }

  useEffect(() => {
    Axios.get(config.API_BASE + "/status/getStatus")
      .then((response) => {
        if (Array.isArray(response.data?.data)) setStatuses(response.data.data);
      })
      .catch((error) => console.log("error", error));
    Axios.get(config.API_BASE + "/user/getUserList", { params: { ifActive: 1 } })
      .then((response) => {
        if (Array.isArray(response.data?.data)) setUsers(response.data.data);
      })
      .catch((error) => console.log("error", error));
  }, []);

  // Public holidays mean no one is expected to mark attendance
  const year = moment(date).format("YYYY");
  useEffect(() => {
    if (holidaysByYear[year]) return;
    Axios.get(
      config.API_BASE +
        "/calender/getHolidayList?" +
        queryString.stringify({ dateBetween: `${year}-01-01|${year}-12-31` }),
    )
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setHolidaysByYear((prev) => ({ ...prev, [year]: list }));
      })
      .catch((error) => console.log("error", error));
  }, [year, holidaysByYear]);

  const holiday = (holidaysByYear[year] || []).find(
    (h) => moment(h.date).format(DAY) === date,
  );
  const isPublicHoliday =
    !!holiday && Number(holiday.type) !== OPTIONAL_HOLIDAY_TYPE;

  const getTaskList = useCallback(() => {
    setLoading(true);
    Axios.post(config.API_BASE + "/user/getUserAttendance", {
      attendanceDate: date,
    })
      .then((response) => {
        const res = response.data;
        setEntries(res.status && Array.isArray(res.data) ? res.data : []);
      })
      .catch((error) => {
        console.log("error", error);
        setEntries([]);
      })
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => {
    getTaskList();
  }, [getTaskList]);

  const statusByName = useMemo(() => {
    const map = {};
    statuses.forEach((s) => (map[normName(s.statusName)] = s));
    return map;
  }, [statuses]);

  const people = useMemo(() => {
    const seen = new Set();
    return entries
      .map((e) => {
        const status = statusByName[normName(e.statusName)] || {};
        return {
          key: e.userId != null ? `id:${e.userId}` : `name:${normName(e.name)}`,
          userId: e.userId,
          name: displayName(e.name, e.email),
          email: e.email || "",
          statusName: e.statusName || "—",
          statusColor: status.statusColor || "#cbd5e1",
          onLeave:
            LEAVE_STATUS_IDS.includes(Number(status.id)) ||
            /leave/i.test(e.statusName || ""),
          task: String(e.taskForDay || "").trim(),
          note: String(e.additionalNote || "").trim(),
        };
      })
      .filter((p) => {
        // One card per person even if the API returns duplicate rows
        if (seen.has(p.key)) return false;
        seen.add(p.key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [entries, statusByName]);

  const notMarked = useMemo(() => {
    const markedIds = new Set(people.map((p) => p.userId).filter((id) => id != null));
    const markedNames = new Set(people.map((p) => normName(p.name)));
    return users
      .filter(
        (u) =>
          !markedIds.has(u.id) &&
          !markedNames.has(normName(displayName(u.name, u.email))),
      )
      .map((u) => ({ key: `id:${u.id}`, name: displayName(u.name, u.email), email: u.email }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [users, people]);

  const statusCounts = useMemo(() => {
    const counts = {};
    people.forEach((p) => {
      if (!counts[p.statusName]) counts[p.statusName] = { count: 0, color: p.statusColor };
      counts[p.statusName].count += 1;
    });
    return Object.entries(counts).sort((a, b) => b[1].count - a[1].count);
  }, [people]);

  const query = normName(search);
  const visible = people.filter(
    (p) =>
      (statusFilter === "all" || p.statusName === statusFilter) &&
      (!query || normName(p.name).includes(query)),
  );
  const visibleNotMarked = notMarked.filter(
    (p) => !query || normName(p.name).includes(query),
  );

  const day = moment(date);
  const isToday = day.isSame(moment(), "day");
  const isWeekend = [0, 6].includes(day.day());
  // Days when nobody is expected to mark attendance
  const isDayOff = isWeekend || isPublicHoliday;
  const onLeaveCount = people.filter((p) => p.onLeave).length;

  return (
    <>
      <Header />
      <div className="container-fluid">
        <div className="tw-mx-auto tw-max-w-7xl tw-py-4 tw-font-sans tw-text-foreground sm:tw-py-6">
          <div className="tw-mb-6 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-3">
            <div>
              <h1 className="tw-m-0 tw-text-2xl tw-font-semibold tw-leading-8 tw-text-foreground">
                {isToday ? "Today's Tasks" : "Team Tasks"}
              </h1>
              <div className="tw-mt-1 tw-text-sm tw-text-muted-foreground">
                What everyone is working on ·{" "}
                <span className="tw-font-medium tw-text-foreground">
                  {day.format("dddd, D MMMM YYYY")}
                </span>
              </div>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <FilterIconButton
                aria-label="Previous day"
                title="Previous day"
                onClick={() => setDate(day.clone().subtract(1, "day").format(DAY))}
              >
                <LuChevronLeft aria-hidden="true" />
              </FilterIconButton>
              <DateFilter
                value={date}
                onChange={(v) => v && setDate(v)}
                onReset={isToday ? undefined : () => setDate(moment().format(DAY))}
                presets={[
                  { label: "Today", value: moment().format(DAY) },
                  { label: "Yesterday", value: moment().subtract(1, "day").format(DAY) },
                ]}
                fieldLabel="Date"
                max={moment().add(1, "month").format(DAY)}
                align="end"
              />
              <FilterIconButton
                aria-label="Next day"
                title="Next day"
                onClick={() => setDate(day.clone().add(1, "day").format(DAY))}
              >
                <LuChevronRight aria-hidden="true" />
              </FilterIconButton>
            </div>
          </div>

          {/* Stats */}
          <div className="tw-mb-6 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-gap-4 lg:tw-grid-cols-4">
            <Stat
              icon={LuUsers}
              tone="tw-bg-primary/10 tw-text-primary"
              label="Marked attendance"
              value={people.length}
              suffix={users.length ? `/ ${users.length}` : ""}
              progress={users.length ? Math.round((people.length / users.length) * 100) : null}
            />
            <Stat
              icon={LuBriefcase}
              tone="tw-bg-emerald-100 tw-text-emerald-700"
              label="Working"
              value={people.length - onLeaveCount}
            />
            <Stat
              icon={LuPalmtree}
              tone="tw-bg-amber-100 tw-text-amber-700"
              label="On leave"
              value={onLeaveCount}
            />
            <Stat
              icon={LuUserX}
              tone="tw-bg-red-50 tw-text-red-600"
              label="Not marked yet"
              value={isDayOff ? "—" : notMarked.length}
            />
          </div>

          {holiday && (
            <div
              className={`tw-mb-5 tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-px-4 tw-py-3 ${
                isPublicHoliday
                  ? "tw-border-amber-200 tw-bg-amber-50"
                  : "tw-border-border tw-bg-white"
              }`}
            >
              <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-amber-100 tw-text-amber-700">
                <LuPalmtree aria-hidden="true" />
              </div>
              <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-foreground">
                  {holiday.holidayName}
                  <span className="tw-ml-2 tw-rounded-full tw-bg-amber-100 tw-px-2 tw-py-px tw-text-[11px] tw-font-semibold tw-text-amber-800">
                    {isPublicHoliday ? "Public holiday" : "Optional holiday"}
                  </span>
                </div>
                <div className="tw-text-xs tw-text-muted-foreground">
                  {isPublicHoliday
                    ? "Office is closed — no one needs to mark attendance on this day."
                    : "Optional holiday — only people who chose to take it are off."}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="tw-mb-5 tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center">
            <div className="tw-relative lg:tw-w-72">
              <LuSearch
                className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                className={`${INPUT} tw-pl-9`}
                placeholder="Search people"
                aria-label="Search people"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-1.5" role="group" aria-label="Filter by status">
              <FilterChip
                active={statusFilter === "all"}
                onClick={() => setStatusFilter("all")}
                label="All"
                count={people.length}
              />
              {statusCounts.map(([name, info]) => (
                <FilterChip
                  key={name}
                  active={statusFilter === name}
                  onClick={() => setStatusFilter(statusFilter === name ? "all" : name)}
                  label={name}
                  count={info.count}
                  color={info.color}
                />
              ))}
            </div>
          </div>

          {loading ? (
            <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="tw-h-44 tw-animate-pulse tw-rounded-lg tw-bg-white/70" />
              ))}
            </div>
          ) : (
            <div className="tw-space-y-6">
              {!isDayOff && visibleNotMarked.length > 0 && statusFilter === "all" && (
                <section className="tw-rounded-lg tw-border tw-border-solid tw-border-red-100 tw-bg-card tw-shadow-soft">
                  <div className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-red-100 tw-bg-red-50/50 tw-px-5 tw-py-3">
                    <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-bg-red-100 tw-text-red-600">
                      <LuUserX aria-hidden="true" />
                    </div>
                    <div>
                      <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-foreground">
                        {isToday ? "Not marked yet" : "Didn't mark attendance"}
                      </h2>
                      <div className="tw-text-xs tw-text-muted-foreground">
                        {visibleNotMarked.length}{" "}
                        {visibleNotMarked.length === 1 ? "person" : "people"} with no
                        attendance for {day.format("D MMM YYYY")}
                      </div>
                    </div>
                  </div>
                  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-2 tw-p-5">
                    {visibleNotMarked.map((p) => (
                      <li
                        key={p.key}
                        className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-border tw-bg-white tw-py-1 tw-pl-1 tw-pr-3"
                        title={p.email || p.name}
                      >
                        <Avatar name={p.name} muted />
                        <span className="tw-text-sm tw-font-medium tw-text-foreground">{p.name}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {visible.length === 0 ? (
                <div className="tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-white tw-px-4 tw-py-12 tw-text-center">
                  <LuClipboardList className="tw-mb-2 tw-text-2xl tw-text-muted-foreground" aria-hidden="true" />
                  <div className="tw-text-sm tw-text-muted-foreground">
                    {people.length === 0
                      ? isPublicHoliday
                        ? `${holiday.holidayName} — office holiday, no tasks logged.`
                        : isWeekend
                        ? "It's the weekend — no tasks logged."
                        : "No one has marked attendance for this day yet."
                      : "No one matches your filters."}
                  </div>
                </div>
              ) : (
                <div className="tw-grid tw-items-start tw-gap-4 lg:tw-grid-cols-2">
                  {visible.map((p) => (
                    <PersonTaskCard key={p.key} person={p} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const Avatar = ({ name, muted = false }) => (
  <span
    className={`tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-select-none tw-items-center tw-justify-center tw-rounded-full tw-text-[11px] tw-font-semibold ${
      muted ? "tw-bg-secondary tw-text-muted-foreground" : "tw-bg-gradient-primary tw-text-white"
    }`}
    aria-hidden="true"
  >
    {getInitials(name)}
  </span>
);

const Stat = ({ icon: Icon, tone, label, value, suffix, progress }) => (
  <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-p-4 tw-shadow-soft sm:tw-p-5">
    <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
      <div className="tw-text-sm tw-font-medium tw-text-muted-foreground">{label}</div>
      <div className={`tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg ${tone}`}>
        <Icon aria-hidden="true" />
      </div>
    </div>
    <div className="tw-mt-2 tw-flex tw-items-baseline tw-gap-1.5">
      <span className="tw-text-3xl tw-font-semibold tw-leading-9 tw-text-foreground">{value}</span>
      {suffix && <span className="tw-text-sm tw-text-muted-foreground">{suffix}</span>}
    </div>
    {typeof progress === "number" && (
      <div className="tw-mt-3 tw-h-1.5 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-secondary">
        <div className="tw-h-full tw-rounded-full tw-bg-gradient-primary" style={{ width: `${progress}%` }} />
      </div>
    )}
  </div>
);

const FilterChip = ({ active, onClick, label, count, color }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-xs tw-font-medium tw-transition-colors ${
      active
        ? "tw-border-primary tw-bg-primary/10 tw-text-primary"
        : "tw-border-border tw-bg-white tw-text-foreground hover:tw-bg-secondary"
    }`}
  >
    {color && <span className="tw-h-2 tw-w-2 tw-rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />}
    {label}
    <span className="tw-text-muted-foreground">{count}</span>
  </button>
);

// Render free-text tasks with light structure: "1. Heading" lines become
// headings, "1.1 ..." / bullet lines are indented, and runs of blank lines
// collapse into a single small gap.
const TaskText = ({ text }) => {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  let gap = false;
  lines.forEach((raw) => {
    const line = raw.trim();
    if (!line) {
      gap = blocks.length > 0;
      return;
    }
    const isHeading = /^\d+[.)]\s+\S/.test(line) && !/^\d+\.\d+/.test(line);
    const isSub = /^\d+\.\d+/.test(line) || /^[-•*–]\s+/.test(line);
    blocks.push({ line, isHeading, isSub, gap });
    gap = false;
  });
  const hasHeadings = blocks.some((b) => b.isHeading);

  return blocks.map((b, i) => (
    <div
      key={i}
      className={`tw-break-words ${
        b.isHeading && hasHeadings
          ? `tw-font-semibold tw-text-foreground ${i > 0 ? "tw-mt-3" : ""}`
          : b.isSub && hasHeadings
            ? "tw-pl-4 tw-text-foreground"
            : `tw-text-foreground ${b.gap ? "tw-mt-2" : ""}`
      }`}
    >
      {b.line}
    </div>
  ));
};

const PersonTaskCard = ({ person }) => {
  const [expanded, setExpanded] = useState(false);
  const lineCount = person.task ? person.task.split(/\r?\n/).length : 0;
  const isLong = lineCount > LONG_TASK_LINES || person.task.length > 600;

  return (
    <section className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-border/60 tw-bg-card tw-shadow-soft">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-px-5 tw-py-3.5">
        <Avatar name={person.name} />
        <div className="tw-min-w-[140px] tw-flex-1">
          <h2 className="tw-m-0 tw-truncate tw-text-base tw-font-semibold tw-leading-6 tw-text-foreground">
            {person.name}
          </h2>
          {person.email && (
            <div className="tw-truncate tw-text-xs tw-text-muted-foreground">{person.email}</div>
          )}
        </div>
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-secondary tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-foreground">
          <span
            className="tw-h-2 tw-w-2 tw-rounded-full"
            style={{ backgroundColor: person.statusColor }}
            aria-hidden="true"
          />
          {person.statusName}
        </span>
      </div>

      <div className="tw-px-5 tw-py-4">
        {person.task ? (
          <>
            <div
              className={`taskText tw-relative tw-text-sm tw-leading-6 tw-text-foreground ${
                isLong && !expanded ? "tw-max-h-48 tw-overflow-hidden" : ""
              }`}
            >
              <TaskText text={person.task} />
              {isLong && !expanded && (
                <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-12 tw-bg-gradient-to-t tw-from-white tw-to-transparent" />
              )}
            </div>
            {isLong && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="tw-mt-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-primary hover:tw-underline"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </>
        ) : (
          <div className="tw-text-sm tw-italic tw-text-muted-foreground">No tasks entered</div>
        )}

        {person.note && (
          <div className="tw-mt-3 tw-flex tw-gap-2 tw-rounded-md tw-bg-amber-50 tw-px-3 tw-py-2 tw-text-xs tw-leading-5 tw-text-amber-900">
            <LuStickyNote className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
            <span className="tw-whitespace-pre-line tw-break-words">{person.note}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default TaskList;
